import React, { Component } from "react";
import { handleDataUpdate, IGreenListForm, IGreenListFormProps, IGreenListFormState } from "./Base";
import Strings from "@my-dld/assets/strings";
import TextInputField from "../../Form/TextInputField";
import { ErrorContainerStateProvider } from "../../Form/FormField";
import { GreenListApi } from "@eres/greenlistapi";
import { showErrorMessage } from "@my-dld/utils/UIUtils";
import { SpinnerLoader } from "../../SpinnerLoader";
import { checkNonEmpty } from "@eres/ejari-web/utils";
import { checkValidationErrorWithMessage } from "@eres/greenlistapi/api";
import { VerifyRequest } from "@eres/greenlistapi/entities";

interface Props extends IGreenListFormProps {
	mobile?: string;
	email?: string;
}

interface KState {
	emailOTP?: string;
	mobileOTP?: string;
}

type ErrorC = { [key in keyof KState]: string };

interface State extends IGreenListFormState, KState {
	isLoading: boolean;
	errorContainer: ErrorC;

}

class Otp extends Component<Props, State> implements IGreenListForm<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true,
			errorContainer: {},
			isValid: false,
		};
	}

	isValid( isUserAction: boolean = false ): boolean {
		const { mobile, email } = this.props;
		const state = this.state;
		const error: { [ k: string ]: string } = {};
		let isValid = true;
		if ( mobile ) {
			isValid = checkNonEmpty( state, "mobileOTP", error ) && isValid;
		}
		if ( email ) {
			isValid = checkNonEmpty( state, "emailOTP", error ) && isValid;
		}

		if ( isUserAction ) this.setState( { errorContainer: error } );
		return isValid;
	}

	setError = ( errors: ErrorC ) => {
		this.setState( {
			errorContainer: {
				...this.state.errorContainer,
				...errors
			}
		} );
	};
	clearError = ( target: "mobile" | "email" | keyof KState ) => {
		const k = target === "mobile" ? "mobileOTP" : target === "email" ? "emailOTP" : target;
		this.setState( {
			errorContainer: {
				...this.state.errorContainer,
				[ k ]: undefined,
			}
		} );
	};

	componentDidMount() {
		this.sendOTP();
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		const oldData = prevState;
		const newData = this.state;
		handleDataUpdate( oldData, newData, prevState, this );
	}

	render() {
		const { mobile, email } = this.props;
		const { isLoading } = this.state;
		return (
			<div className="list-terms modal-body-passcode form-row">
				<div className="alert alert-info">
					{ Strings.update_profile_note }
				</div>
				{ isLoading && <SpinnerLoader/> }
				{ !isLoading && <>
					{ email && <>
						<div className="col-md-12">
							<OTPView
								holder={ this }
								id={ "emailOTP" }
								target={ email }
								onResend={ this.resendEmailOTP }
							/>
						</div>
					</>
					}
					{ email && mobile && <>
						<div className="col-md-12">
							<hr/>
						</div>
					</> }
					{ mobile && <>
						<div className="col-md-12">
							<OTPView
								holder={ this }
								id={ "mobileOTP" }
								target={ mobile }
								onResend={ this.resendMobileOTP }
							/>
						</div>
					</>
					}
				</> }
			</div>
		);
	}

	setLoading = ( isLoading: boolean ) => {
		this.setState( { isLoading: isLoading } );
	};

	//region OTP Generators
	sendOTP = () => {
		const tasks: Promise<any>[] = [];
		const { mobile, email } = this.props;
		this.setLoading( true );
		if ( mobile ) {
			tasks.push( this.generateMobileOTP() );
		}
		if ( email ) {
			tasks.push( this.generateEmailOTP() );
		}
		Promise.all( tasks ).then( _results => {
			this.setLoading( false );
		} ).catch( e => {
			this.setLoading( false );
			showErrorMessage( e );
		} );
	};
	resendEmailOTP = () => this.resendOTP( "email" );
	resendMobileOTP = () => this.resendOTP( "mobile" );
	resendOTP = ( target: "mobile" | "email" ) => {
		this.setLoading( true );
		this.clearError( target );
		this.generateOTP( target ).then( _x => {
			this.setLoading( false );
		} ).catch( e => {
			this.setLoading( false );
			showErrorMessage( e );
		} );
	};
	generateEmailOTP = () => this.generateOTP( "email" );
	generateMobileOTP = () => this.generateOTP( "mobile" );
	generateOTP = ( target: "mobile" | "email" ) => {
		const request = target === "mobile" ? { Mobile: this.props.mobile } : { Email: this.props.email };
		return GreenListApi.generateOTP( request );
	};
	//endregion

	//region OTP Verification
	verifyOTP = () => {
		const { mobile, email } = this.props;
		this.setLoading( true );
		const tasks: Promise<boolean>[] = [];
		this.setLoading( true );
		if ( mobile ) {
			tasks.push( this.verifyMobileOTP() );
		}
		if ( email ) {
			tasks.push( this.verifyEmailOTP() );
		}
		return Promise.all( tasks ).then( results => {
			const r = results.reduce( ( p, c ) => p && c, true );
			this.setLoading( false );
			return Promise.resolve( r );
		} ).catch( _e => {
			this.setLoading( false );
			return Promise.resolve( false );
		} );
	};

	verifyOtp = ( request: VerifyRequest, key: keyof KState ) => {
		this.clearError( key );
		return GreenListApi.verifyOTP( request ).then( checkValidationErrorWithMessage ).then( _r => {
			return Promise.resolve( true );
		} ).catch( ex => {
			console.error( ex );
			this.setError( { [ key ]: Strings.invalid_otp } );
			return Promise.resolve( false );
		} );
	};
	verifyEmailOTP = () => {
		return this.verifyOtp( { Email: this.props.email, otp: this.state.emailOTP! }, "emailOTP" );
	};
	verifyMobileOTP = () => {
		return this.verifyOtp( { Mobile: this.props.mobile, otp: this.state.mobileOTP! }, "mobileOTP" );
	};

}

const formProps = {
	containerClassName: "form-group mb-4 field-style",
	inputClassName: "form-control",
	errorProvider: ErrorContainerStateProvider,
};

interface OTPViewProps {
	id: string,
	holder: any;
	target: string;
	onResend: () => void;
}

function OTPView( props: OTPViewProps ) {
	const { holder, target, id, onResend } = props;
	return <>
		<p className="text-center my-4">
			{ Strings.please_enter_the_otp_on + " " + target }
		</p>
		<div className="position-relative mb-4 otp-field">
			<TextInputField
				{ ...formProps }
				holder={ holder }
				placeHolder={ "" }
				id={ id }
				label={ Strings.enter_otp }
				required={ true }
				maxLength={ 10 }
				containerClassName="form-group field-style mb-0"
			/>
			<button type="button" className="btn btn-link p-0 btn-otp-greenlist" onClick={ onResend }>
				{ Strings.resend }
			</button>
		</div>
	</>;
}

export default Otp;
